import React, {useEffect} from 'react'
import {FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {deleteWizardDocument, downloadWizardDocument, uploadWizardDocument} from '../../api'
import {IApplicationFormData, IWizardDocument} from '../../models/application-form'
import UploadSlotRow from './sections/UploadSlotRow'
import WizardSectionCard from './sections/WizardSectionCard'
import YesNoChoice from './sections/YesNoChoice'
import {
  UploadSlotDefinition,
  UPLOAD_SLOT_TYPES,
  identityUploadSlots,
  incomeUploadSlots,
  internationalStudentUploadSlots,
  visaUploadSlots,
  companyUploadSlots,
  benefitUploadSlots,
} from './sections/uploadSlots'
import YesNoSwitch from './sections/YesNoSwitch'

interface UploadsStepProps {
  register: UseFormRegister<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const allSlotOrder: string[] = [
  ...identityUploadSlots,
  ...incomeUploadSlots,
  ...benefitUploadSlots,
  ...visaUploadSlots,
  ...internationalStudentUploadSlots,
  ...companyUploadSlots,
].map((slot) => slot.type)

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024
const ALLOWED_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,application/pdf'
const ALLOWED_UPLOAD_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'application/pdf'])
const ALLOWED_UPLOAD_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.pdf'])
const UPLOAD_LATER_SLOT_TYPES = new Set<string>([
  UPLOAD_SLOT_TYPES.incomeDoc1,
  UPLOAD_SLOT_TYPES.incomeDoc2,
  UPLOAD_SLOT_TYPES.incomeStatement,
  UPLOAD_SLOT_TYPES.visaGrant,
])

type UploadSectionKey = 'identity' | 'income' | 'benefit' | 'visa' | 'international' | 'company'

const hasAllowedUploadType = (file: File): boolean => {
  const normalizedType = (file.type ?? '').trim().toLowerCase()
  if (ALLOWED_UPLOAD_MIME_TYPES.has(normalizedType)) {
    return true
  }

  const fileName = (file.name ?? '').toLowerCase()
  const extension = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : ''
  return ALLOWED_UPLOAD_EXTENSIONS.has(extension)
}

const getSectionForSlotType = (slotType: string): UploadSectionKey => {
  if (identityUploadSlots.some((slot) => slot.type === slotType)) {
    return 'identity'
  }

  if (incomeUploadSlots.some((slot) => slot.type === slotType)) {
    return 'income'
  }

  if (benefitUploadSlots.some((slot) => slot.type === slotType)) {
    return 'benefit'
  }

  if (visaUploadSlots.some((slot) => slot.type === slotType)) {
    return 'visa'
  }

  if (internationalStudentUploadSlots.some((slot) => slot.type === slotType)) {
    return 'international'
  }

  return 'company'
}

const UploadsStep: React.FC<UploadsStepProps> = ({errors, setValue, watch, triggerValidation}) => {
  const sessionId = watch('sessionId')
  const applicationType = watch('applicationType')
  const documents = watch('documents') ?? []
  const incomeUploadLaterTypes = watch('incomeUploadLaterTypes') ?? []
  const savedIdentificationType = watch('identificationType')
  const savedIsVisaHolder = watch('isVisaHolder')
  const savedIsInternationalStudent = watch('isInternationalStudent')
  const income = watch('income')
  const documentsError = errors?.documents as {message?: string} | undefined
  const identificationTypeError =
    typeof errors?.identificationType?.message === 'string'
      ? errors.identificationType.message
      : undefined
  const [identityType, setIdentityType] = React.useState(() => {
    const normalized = (savedIdentificationType ?? '').trim()
    return normalized
  })
  const [isInternationalStudent, setIsInternationalStudent] = React.useState(() =>
    typeof savedIsInternationalStudent === 'boolean'
      ? savedIsInternationalStudent
      : Boolean(documents.find((doc) => doc.type === UPLOAD_SLOT_TYPES.internationalCoe))
  )
  const [uploadMessagesBySection, setUploadMessagesBySection] = React.useState<
    Partial<Record<UploadSectionKey, string>>
  >({})
  const [uploadingByType, setUploadingByType] = React.useState<Record<string, boolean>>({})
  const [uploadProgressByType, setUploadProgressByType] = React.useState<Record<string, number>>({})
  const [previewUrlByType, setPreviewUrlByType] = React.useState<Record<string, string>>({})
  const previewUrlRef = React.useRef<Record<string, string>>({})

  const setSectionUploadMessage = (section: UploadSectionKey, message?: string) => {
    setUploadMessagesBySection((current) => {
      const next = {...current}
      if (message) {
        next[section] = message
      } else {
        delete next[section]
      }

      return next
    })
  }

  const getSectionUploadMessage = (section: UploadSectionKey): string | undefined =>
    uploadMessagesBySection[section]

  const uploadedDocumentTypes = React.useMemo(
    () =>
      new Set(documents.filter((item) => !!item?.filename?.trim()).map((item) => item?.type ?? '')),
    [documents]
  )
  const uploadLaterTypeSet = React.useMemo(
    () => new Set(incomeUploadLaterTypes),
    [incomeUploadLaterTypes]
  )

  const hasUploadedType = (type: string): boolean => uploadedDocumentTypes.has(type)
  const hasCompletedUploadSlot = (type: string): boolean =>
    hasUploadedType(type) || uploadLaterTypeSet.has(type)
  const hasValidationError = Boolean(documentsError?.message)
  const isCompanyTrustApplication = applicationType === 'CompanyTrust'
  const hasBenefitIncome =
    income?.hasPension === true ||
    income?.isCentreLink === true ||
    income?.hasNdis === true ||
    income?.hasStudentSupport === true
  const selectedBenefitIncomeCount = [
    income?.hasPension === true,
    income?.hasStudentSupport === true,
    income?.isCentreLink === true,
    income?.hasNdis === true,
  ].filter(Boolean).length
  const benefitSectionTitle =
    selectedBenefitIncomeCount > 1
      ? 'Proof of Government Benefits'
      : income?.hasPension === true
        ? 'Proof of Pension'
        : income?.hasStudentSupport === true
          ? 'Proof of Student Support'
          : 'Proof of Centrelink payments'
  const firstMissingUploadType = React.useMemo(() => {
    const requiredVisibleUploadTypes: string[] = [
      UPLOAD_SLOT_TYPES.identityFront,
      UPLOAD_SLOT_TYPES.identityBack,
      UPLOAD_SLOT_TYPES.incomeDoc1,
      UPLOAD_SLOT_TYPES.incomeDoc2,
    ]

    if (hasBenefitIncome) {
      requiredVisibleUploadTypes.push(UPLOAD_SLOT_TYPES.incomeStatement)
    }

    if (savedIsVisaHolder) {
      requiredVisibleUploadTypes.push(UPLOAD_SLOT_TYPES.visaGrant)
    }

    if (isInternationalStudent) {
      requiredVisibleUploadTypes.push(
        UPLOAD_SLOT_TYPES.internationalCoe,
        UPLOAD_SLOT_TYPES.enrollmentLetter
      )
    }

    if (isCompanyTrustApplication) {
      requiredVisibleUploadTypes.push(UPLOAD_SLOT_TYPES.letterOfAuthority)
    }

    return requiredVisibleUploadTypes.find((type) => !uploadedDocumentTypes.has(type))
  }, [
    hasBenefitIncome,
    isCompanyTrustApplication,
    isInternationalStudent,
    savedIsVisaHolder,
    uploadedDocumentTypes,
  ])

  const validationMessagesBySection: Partial<Record<UploadSectionKey, string>> = {
    identity:
      identificationTypeError ??
      (hasValidationError &&
      (!hasUploadedType(UPLOAD_SLOT_TYPES.identityFront) ||
        !hasUploadedType(UPLOAD_SLOT_TYPES.identityBack))
        ? 'Upload your ID front and back, and pick the ID type.'
        : undefined),
    income:
      hasValidationError &&
      (!hasCompletedUploadSlot(UPLOAD_SLOT_TYPES.incomeDoc1) ||
        !hasCompletedUploadSlot(UPLOAD_SLOT_TYPES.incomeDoc2))
        ? 'Upload your income proof or tick "I\'ll add this later".'
        : undefined,
    benefit:
      hasValidationError &&
      hasBenefitIncome &&
      !hasCompletedUploadSlot(UPLOAD_SLOT_TYPES.incomeStatement)
        ? 'Upload your income proof or  "I\'ll add this later".'
        : undefined,
    visa:
      hasValidationError &&
      savedIsVisaHolder &&
      !hasCompletedUploadSlot(UPLOAD_SLOT_TYPES.visaGrant)
        ? 'Please upload your visa document or save it for later.'
        : undefined,
    international:
      hasValidationError &&
      isInternationalStudent &&
      (!hasUploadedType(UPLOAD_SLOT_TYPES.internationalCoe) ||
        !hasUploadedType(UPLOAD_SLOT_TYPES.enrollmentLetter))
        ? 'Please upload both international student documents (CoE and Enrolment Letter).'
        : undefined,
    company:
      hasValidationError &&
      isCompanyTrustApplication &&
      !hasUploadedType(UPLOAD_SLOT_TYPES.letterOfAuthority)
        ? 'Please upload the letter of authority.'
        : undefined,
  }

  const getSectionErrorMessage = (section: UploadSectionKey): string | undefined =>
    getSectionUploadMessage(section) ?? validationMessagesBySection[section]

  const setSlotSavedForLater = (slotType: string, isSavedForLater: boolean) => {
    if (!UPLOAD_LATER_SLOT_TYPES.has(slotType)) {
      return
    }

    if (uploadLaterTypeSet.has(slotType) === isSavedForLater) {
      return
    }

    const nextTypes = isSavedForLater
      ? Array.from(new Set([...incomeUploadLaterTypes, slotType]))
      : incomeUploadLaterTypes.filter((type) => type !== slotType)

    setSectionUploadMessage(getSectionForSlotType(slotType))
    setValue('incomeUploadLaterTypes', nextTypes, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const saveSlotForLater = (slotType: string) => {
    if (hasUploadedType(slotType)) {
      return
    }

    setSlotSavedForLater(slotType, true)
  }

  const undoSlotSavedForLater = (slotType: string) => {
    setSlotSavedForLater(slotType, false)
  }

  const setInternationalStudent = (enabled: boolean) => {
    setIsInternationalStudent(enabled)
    setSectionUploadMessage('international')
    if (!enabled) {
      void clearSlotGroup(internationalStudentUploadSlots)
    }
    setValue('isInternationalStudent', enabled, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  React.useEffect(
    () => () => {
      Object.values(previewUrlRef.current).forEach((url) => {
        URL.revokeObjectURL(url)
      })
    },
    []
  )

  React.useEffect(() => {
    const normalized = (savedIdentificationType ?? '').trim()
    setIdentityType(normalized)
  }, [savedIdentificationType])

  React.useEffect(() => {
    const hasVisaDocument = documents.some(
      (document) => document.type === UPLOAD_SLOT_TYPES.visaGrant
    )
    const hasInternationalStudentDocument = documents.some(
      (document) => document.type === UPLOAD_SLOT_TYPES.internationalCoe
    )

    if (typeof savedIsInternationalStudent === 'boolean') {
      setIsInternationalStudent(savedIsInternationalStudent)
    } else {
      setIsInternationalStudent(hasInternationalStudentDocument)
      setValue('isInternationalStudent', hasInternationalStudentDocument, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }
  }, [documents, savedIsInternationalStudent, savedIsVisaHolder, setValue])

  const sortDocuments = (items: IWizardDocument[]): IWizardDocument[] => {
    return [...items].sort((left, right) => {
      const leftIndex = allSlotOrder.indexOf(left.type ?? '')
      const rightIndex = allSlotOrder.indexOf(right.type ?? '')
      const normalizedLeft = leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex
      const normalizedRight = rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex
      return normalizedLeft - normalizedRight
    })
  }

  const setSlotPreviewUrl = (slotType: string, url?: string) => {
    setPreviewUrlByType((current) => {
      const previous = current[slotType]
      if (previous && previous !== url) {
        URL.revokeObjectURL(previous)
      }

      const next = {...current}
      if (url) {
        next[slotType] = url
      } else {
        delete next[slotType]
      }

      previewUrlRef.current = next
      return next
    })
  }

  const upsertDocument = async (slotType: string, file: File) => {
    const section = getSectionForSlotType(slotType)
    if (!sessionId) {
      setSectionUploadMessage(section, 'Session expired. Please verify your mobile again.')
      return
    }

    if (!hasAllowedUploadType(file)) {
      setSectionUploadMessage(section, 'Only PNG, JPG, JPEG, and PDF files are allowed.')
      return
    }

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setSectionUploadMessage(section, 'File size must be 10MB or less.')
      return
    }

    const existing = documents.find((document) => document.type === slotType)
    setSectionUploadMessage(section)
    setUploadingByType((current) => ({
      ...current,
      [slotType]: true,
    }))
    setUploadProgressByType((current) => ({
      ...current,
      [slotType]: 0,
    }))

    try {
      const uploaded = await uploadWizardDocument({
        sessionId,
        type: slotType,
        file,
        documentId: existing?.documentId,
        onProgress: (progressPercent) => {
          setUploadProgressByType((current) => ({
            ...current,
            [slotType]: progressPercent,
          }))
        },
      })

      const updated: IWizardDocument = {
        ...existing,
        ...uploaded,
        type: slotType,
        filename: uploaded.filename || file.name,
        contentType: uploaded.contentType || file.type || 'application/octet-stream',
        verificationStatus: uploaded.verificationStatus || 'uploaded',
        uploadedAtUtc: uploaded.uploadedAtUtc || new Date().toISOString(),
      }

      const nextList = sortDocuments([
        ...documents.filter((document) => document.type !== slotType),
        updated,
      ])

      setValue('documents', nextList, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      setSlotSavedForLater(slotType, false)

      if ((file.type || '').toLowerCase().startsWith('image/')) {
        setSlotPreviewUrl(slotType, URL.createObjectURL(file))
      } else {
        setSlotPreviewUrl(slotType)
      }
    } catch {
      setSectionUploadMessage(section, 'Unable to upload document right now.')
    } finally {
      setUploadingByType((current) => ({
        ...current,
        [slotType]: false,
      }))
      setUploadProgressByType((current) => ({
        ...current,
        [slotType]: 0,
      }))
    }
  }

  const clearSlot = async (slotType: string) => {
    const section = getSectionForSlotType(slotType)
    const existing = documents.find((document) => document.type === slotType)
    if (existing?.documentId && sessionId) {
      try {
        await deleteWizardDocument({
          sessionId,
          documentId: existing.documentId,
        })
      } catch {
        setSectionUploadMessage(section, 'Unable to remove document right now.')
        return
      }
    }

    setValue(
      'documents',
      documents.filter((document) => document.type !== slotType),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )

    setSlotPreviewUrl(slotType)
    setSectionUploadMessage(section)
  }

  const previewDocument = async (document?: IWizardDocument, slotType?: string) => {
    const section = slotType ? getSectionForSlotType(slotType) : undefined
    if (!sessionId || !document?.documentId) {
      return
    }

    try {
      const blob = await downloadWizardDocument(sessionId, document.documentId)
      const previewUrl = URL.createObjectURL(blob)
      window.open(previewUrl, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000)
    } catch {
      if (section) {
        setSectionUploadMessage(section, 'Unable to preview document right now.')
      }
    }
  }

  const clearSlotGroup = async (slots: UploadSlotDefinition[]) => {
    const section = getSectionForSlotType(slots[0]?.type ?? '')
    const slotTypes = slots.map((slot) => slot.type as string)
    const documentsToRemove = documents.filter((document) =>
      slotTypes.includes(document.type ?? '')
    )

    if (sessionId) {
      for (const document of documentsToRemove) {
        if (!document.documentId) {
          continue
        }

        try {
          await deleteWizardDocument({
            sessionId,
            documentId: document.documentId,
          })
        } catch {
          setSectionUploadMessage(section, 'Unable to remove one or more documents right now.')
          return
        }
      }
    }

    setValue(
      'documents',
      documents.filter((document) => !slotTypes.includes(document.type ?? '')),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )

    slotTypes.forEach((slotType) => setSlotPreviewUrl(slotType))
    setSectionUploadMessage(section)
  }

  const renderSlots = (slots: UploadSlotDefinition[]) => (
    <div className='d-grid gap-3'>
      {slots.map((slot, index) => {
        const existing = documents.find((document) => document.type === slot.type)
        const canSaveForLater = UPLOAD_LATER_SLOT_TYPES.has(slot.type)
        const isFirstMissingUpload = slot.type === firstMissingUploadType
        return (
          <div
            key={`${slot.type}-${index}`}
            data-upload-slot-type={slot.type}
            data-wizard-scroll-target={isFirstMissingUpload ? 'first-missing-upload' : undefined}
          >
            <UploadSlotRow
              key={slot.type}
              slot={slot}
              document={existing}
              documentName={slot.name}
              isUploading={Boolean(uploadingByType[slot.type])}
              uploadProgress={uploadProgressByType[slot.type] ?? 0}
              previewUrl={previewUrlByType[slot.type]}
              accept={ALLOWED_UPLOAD_ACCEPT}
              allowPreview={slot.allowPreview}
              isSavedForLater={canSaveForLater && uploadLaterTypeSet.has(slot.type)}
              onUploadFile={(file) => {
                void upsertDocument(slot.type, file)
              }}
              onPreview={() => {
                void previewDocument(existing, slot.type)
              }}
              onClear={() => {
                void clearSlot(slot.type)
              }}
              onSaveForLater={canSaveForLater ? () => saveSlotForLater(slot.type) : undefined}
              onUndoSaveForLater={
                canSaveForLater ? () => undoSlotSavedForLater(slot.type) : undefined
              }
            />
          </div>
        )
      })}
    </div>
  )

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  const identitySectionErrorMessage = getSectionErrorMessage('identity')

  return (
    <div>
      <div className='wizard-hint-card black mb-4 d-flex' style={{padding: '20px'}}>
        <i className='bi bi-shield-lock me-5 gradient-icon'></i>
        <span>
          <strong>Your documents are encrypted.</strong> Only shared when you send your card. Revoke
          access anytime.
        </span>
      </div>

      <div className='wizard-hint-card green mb-4 d-flex' style={{padding: '20px'}}>
        <svg
          width='16'
          height='16'
          viewBox='0 0 20 20'
          fill='none'
          stroke='#0A7C5A'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M3.5 10.5l4 4 9-9'></path>
        </svg>
        <span className='ms-3'>
          <strong>Nearly there.</strong> Upload your ID and proof of income, then you're done.
        </span>
      </div>

      <div className='d-grid gap-4'>
        <WizardSectionCard
          title='Proof of identity'
          isRequired={true}
          subTitle='Pick your ID type and snap a photo.'
          isValid={identitySectionErrorMessage === undefined}
          className='p-8'
        >
          <div className='mb-3 mt-5'>
            <label className='form-label fw-semibold'>
              ID Type <span className='text-danger'>*</span>
            </label>
            <select
              className={`form-select ${identificationTypeError ? 'is-invalid' : ''}`}
              value={identityType}
              required
              aria-invalid={Boolean(identificationTypeError)}
              onChange={(event) => {
                const selectedType = event.target.value
                setIdentityType(selectedType)
                setValue('identificationType', selectedType, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }}
            >
              <option value=''>Select</option>
              <option value="Australian Driver's Licence">Australian Driver's Licence</option>
              <option value='Australian Passport'>Australian Passport</option>
              <option value='Proof of Age / Photo Card'>Proof of Age / Photo Card</option>
              <option value='Foreign Passport'>Foreign Passport</option>
            </select>
            {identificationTypeError && (
              <div className='text-danger small mt-1'>{identificationTypeError}</div>
            )}
          </div>

          {renderSlots(identityUploadSlots)}
          {identitySectionErrorMessage &&
            identitySectionErrorMessage !== identificationTypeError && (
              <div className='text-danger2 fs-6 mt-3'>{identitySectionErrorMessage}</div>
            )}
        </WizardSectionCard>

        <div data-wizard-scroll-target='proof-of-employment'>
          <WizardSectionCard
            title='Proof of Employment'
            subTitle='Payslips or a bank statement from the last 3 months, a letter from your employer, or your employment contract.'
            isRequired={true}
            isValid={getSectionErrorMessage('income') === undefined}
            className='p-8'
          >
            {renderSlots(incomeUploadSlots)}
            <div className='text-muted mb-4 mt-0 fs-6'>
              A third document. <strong className='text-grad'>Strongly recommended.</strong>
            </div>
            {getSectionErrorMessage('income') && (
              <div className='text-danger2 fs-6 mt-3'>{getSectionErrorMessage('income')}</div>
            )}
          </WizardSectionCard>
        </div>

        {hasBenefitIncome && (
          <div data-wizard-scroll-target='proof-of-benefits'>
            <WizardSectionCard
              title={benefitSectionTitle}
              isRequired={true}
              isValid={getSectionErrorMessage('benefit') === undefined}
              className='p-8'
            >
              {renderSlots(benefitUploadSlots)}
              {getSectionErrorMessage('benefit') && (
                <div className='text-danger2 fs-6 mt-3'>{getSectionErrorMessage('benefit')}</div>
              )}
            </WizardSectionCard>
          </div>
        )}

        {savedIsVisaHolder && (
          <WizardSectionCard
            title='Visa Grant Notice'
            subTitle='You marked yourself as a visa holder. Upload your Grant Notice below'
            className='p-8'
          >
            <div className='mt-3'>
              {renderSlots(
                visaUploadSlots.map((slot) => ({
                  ...slot,
                  required: false,
                  optional: false,
                }))
              )}
            </div>
            {getSectionErrorMessage('visa') && (
              <div className='text-danger2 mt-3'>{getSectionErrorMessage('visa')}</div>
            )}
          </WizardSectionCard>
        )}

        {isCompanyTrustApplication && (
          <WizardSectionCard
            title='Company/Trust'
            isRequired={isCompanyTrustApplication}
            isValid={getSectionErrorMessage('company') === undefined}
            className='p-8'
          >
            <div className='small text-muted mb-3'>Upload letter of authority document.</div>
            {renderSlots(
              isCompanyTrustApplication
                ? companyUploadSlots.map((slot) => ({
                    ...slot,
                    required: true,
                    optional: false,
                  }))
                : companyUploadSlots
            )}
            {getSectionErrorMessage('company') && (
              <div className='text-danger2 mt-3'>{getSectionErrorMessage('company')}</div>
            )}
          </WizardSectionCard>
        )}
      </div>
    </div>
  )
}

export default UploadsStep
